import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { History, ChevronRight } from "lucide-react";

const ACTION_LABELS = {
  "engagement.note_add":              { label: "Note added",         tone: "ontrack" },
  "engagement.note_delete":           { label: "Note deleted",       tone: "behind" },
  "engagement.issue_add":             { label: "Issue added",        tone: "amber" },
  "engagement.issue_delete":          { label: "Issue deleted",      tone: "behind" },
  "engagement.hub_set":               { label: "Hub changed",        tone: "ink" },
  "engagement.cluster_set":           { label: "Cluster changed",    tone: "ink" },
  "engagement.internal_project_set":  { label: "Internal project",   tone: "ink" },
  "engagement.flag_set":              { label: "Flag changed",       tone: "amber" },
  "engagement.scope_set":             { label: "Scope changed",      tone: "ink" },
};

function tone(t) {
  return t === "ontrack" ? "var(--on-track)"
    : t === "amber"      ? "var(--amber)"
    : t === "behind"     ? "var(--behind)"
    : "var(--ink-2)";
}

export default function AnnotationAudit() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    api.get("/audit/annotations?limit=1000")
      .then((r) => setRows(r.data || []))
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail) || "Failed"))
      .finally(() => setLoading(false));
  }, []);

  const visible = rows.filter((r) => {
    if (!filter) return true;
    const s = `${r.action || ""} ${r.target_label || ""} ${r.actor_email || ""}`.toLowerCase();
    return s.includes(filter.toLowerCase());
  });

  return (
    <div className="max-w-[1200px] mx-auto px-10 py-10 fade-in-up" data-testid="annotation-audit-page">
      <div className="mb-8">
        <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mb-2">
          Admin · Data Provenance
        </div>
        <h1 className="serif text-5xl tracking-tight leading-none">Annotations Audit Trail</h1>
        <p className="text-sm text-[color:var(--ink-2)] mt-3 max-w-2xl">
          Every note, issue, hub, cluster, internal-project, flag and scope change on a language
          engagement — with old → new values and the author.
        </p>
      </div>

      <div className="mb-4 flex items-center gap-3">
        <input
          className="input-flat flex-1 max-w-md"
          placeholder="Filter by action, language, or author…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          data-testid="audit-filter"
        />
        <div className="mono text-[10px] text-[color:var(--ink-3)]">
          {loading ? "Loading…" : `${visible.length} of ${rows.length} entries`}
        </div>
      </div>

      <div className="hairline bg-white overflow-x-auto">
        <table className="w-full data-table min-w-[1000px]" data-testid="audit-table">
          <thead>
            <tr>
              <th className="!py-2">When</th>
              <th className="!py-2">Action</th>
              <th className="!py-2">Language</th>
              <th className="!py-2">Old → New</th>
              <th className="!py-2">By</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="text-center py-8 mono text-xs">Loading…</td></tr>
            ) : visible.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-8 text-[color:var(--ink-2)] italic">No changes yet.</td></tr>
            ) : visible.map((r, i) => {
              const meta = ACTION_LABELS[r.action] || { label: r.action, tone: "ink" };
              const diff = r.changes || {};
              const firstKey = Object.keys(diff)[0];
              const changes = firstKey ? diff[firstKey] : {};
              return (
                <tr key={i} data-testid={`audit-row-${i}`}>
                  <td className="mono text-[10px] text-[color:var(--ink-3)]">
                    {r.timestamp ? new Date(r.timestamp).toLocaleString() : "—"}
                  </td>
                  <td>
                    <span className="mono text-[10px] uppercase tracking-widest" style={{ color: tone(meta.tone) }}>
                      {meta.label}
                    </span>
                  </td>
                  <td className="text-sm">{r.target_label || <span className="text-[color:var(--ink-3)]">—</span>}</td>
                  <td className="text-xs">
                    <DiffCell k={firstKey} old={changes.old} nw={changes.new} deleted={changes.deleted} added={changes.new} />
                  </td>
                  <td className="mono text-[10px] text-[color:var(--ink-2)]">{r.actor_email || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function DiffCell({ k, old, nw, deleted }) {
  const fmt = (v) => {
    if (v === undefined || v === null || v === "") return <span className="text-[color:var(--ink-3)]">∅</span>;
    if (Array.isArray(v)) return v.length ? v.join(", ") : <span className="text-[color:var(--ink-3)]">∅</span>;
    return String(v);
  };
  if (deleted !== undefined) {
    return <span className="line-through text-[color:var(--behind)]">{fmt(deleted)}</span>;
  }
  if (k === "note" || k === "issue") {
    return <span>{fmt(nw)}</span>;
  }
  return (
    <span className="flex items-center gap-2 flex-wrap">
      <span className="text-[color:var(--ink-3)]">{fmt(old)}</span>
      <ChevronRight size={12} className="text-[color:var(--ink-3)]" />
      <span>{fmt(nw)}</span>
    </span>
  );
}
